import { isTranslationSlot, type TranslationSlot } from "./elements";
import type { StoredTranslationRow } from "./translation-state";

/**
 * One locale's rows, addressed by `(element_key, slot, source_hash)`. The hash is both
 * guard and version selector: several rows can exist for one (key, slot) at different
 * hashes, so a published version still draining and a freshly translated draft each
 * resolve the row written against their own bytes — no version column is consulted.
 *
 * A `Map` on a composite key, not nested plain objects, because element keys are
 * attacker-influenced (hand-authoring a tag is supported), so a key can legitimately
 * read `constructor`. Keyed on a plain `{}`, `map[key][slot] = …` would resolve
 * `map[key]` to a shared built-in and write onto it, process-wide.
 *
 * There is deliberately no selection filter: a deselected element carries `off` in its
 * own tag and short-circuits before the lookup, which is what stops a draft's
 * deselection from retroactively muting a campaign that is still draining.
 */
export type TranslationsForLocale = Map<string, string>;

export type TranslationAddress = {
  elementKey: string;
  slot: TranslationSlot;
  sourceHash: string;
};

export type TranslationRow = TranslationAddress & { value: string };

/** A stored row as a render row, or none when its slot is one no tag carries. */
export function toTranslationRow(
  row: Pick<
    StoredTranslationRow,
    "element_key" | "slot" | "source_hash" | "value"
  >,
): TranslationRow[] {
  return isTranslationSlot(row.slot)
    ? [
        {
          elementKey: row.element_key,
          slot: row.slot,
          sourceHash: row.source_hash,
          value: row.value,
        },
      ]
    : [];
}

/**
 * `:` is unambiguous because a key is `ELEMENT_KEY_CHARS` (`A-Za-z0-9._-`), a slot is a
 * bare word and a hash is hex. None admits the separator, so no two addresses collide.
 */
export function translationAddress(row: TranslationAddress): string {
  return `${row.elementKey}:${row.slot}:${row.sourceHash}`;
}

export function buildTranslationsForLocale(
  rows: TranslationRow[],
): TranslationsForLocale {
  const map: TranslationsForLocale = new Map();
  for (const row of rows) {
    // First writer wins. Two rows at one address are the same element's same text
    // translated twice into one locale — the unique index makes that impossible from
    // the upload path, so either value is equally correct.
    if (!map.has(translationAddress(row))) {
      map.set(translationAddress(row), row.value);
    }
  }
  return map;
}

/** The translation for one element's slot at its CURRENT text, or undefined. */
export function lookupTranslation(
  translations: TranslationsForLocale | undefined,
  args: TranslationAddress,
): string | undefined {
  return translations?.get(translationAddress(args));
}
